// WALLS (3.20.0). Owner, 2026-10-21: "update tracecad with a wall drawing tool for easy layout of walls. Also allow easy
// adding and removing of doors to the walls allow for single double and roller doors. walls should be filled with
// patterned fill".
//
// A WALL RUN is ONE group with meta.wall = WallMeta. Its children, in this order:
//   [0]   the SPINE: a hidden polyline, the line the person drew (the centre or a face of the wall, see `justify`).
//         Moves, turns, copies and pastes carry it like any line, so the wall needs no code of its own for them.
//   [1..] DERIVED parts, made from the spine and meta.wall by wallParts(), each with meta.wallPart:
//         "piece" - a closed outline of the wall between two openings (or ends), with the patterned fill;
//         "door"  - the lines and arcs of a door symbol.
// After every command, wallDeriver() draws again each wall whose parts do not match its spine and meta (a door added,
// a thickness changed, a length typed). The parts are never edited by hand: an edit to them is drawn back.
//
// GEOMETRY. The two faces are offset lines of the spine. Corners are mitred (the face lines meet), so a run of walls
// has one clean outline with no seam at a corner. A closed run (a room) with no door is a ring: one outline that goes
// round the outer face, across the wall at the first corner, and round the inner face the other way (the fill and the
// hit test leave the room inside it empty). A door cuts the wall square across; the cut ends are the jambs.
//
// LEFT and RIGHT are as you travel along the spine, on the screen (Y down): travelling right, left is up.
// Pure: no store, no UI. tests/walls.test.ts covers it.
import type { Vec } from "../core/geometry";
import type { Doc, Entity, FillMode, GroupEntity, PolylineEntity, ArcEntity, Style } from "../doc/entities";
import { makeDeleteCommand, makeUngroupCommand, type Command } from "../doc/commands";
import { newId } from "../doc/document";

export type DoorKind = "single" | "double" | "roller";
export type WallJustify = "centre" | "left" | "right";

export interface WallDoor {
  id: string;
  kind: DoorKind;
  /** The segment of the spine (0 = from point 0 to point 1). */
  seg: number;
  /** The centre of the opening, mm along the segment from its start point. */
  at: number;
  /** The clear opening, mm. */
  width: number;
  /** The side of the wall the door opens to (a roller door: the side of its drum): 1 = left, -1 = right. */
  side: 1 | -1;
  /** A single door: the jamb with the hinges, at the start or the end side of the opening. */
  hinge: "start" | "end";
}

export interface WallMeta {
  /** mm */
  thickness: number;
  /** The spine is the centre of the wall, or its left or right face. */
  justify: WallJustify;
  closed: boolean;
  /** The patterned fill of the wall. */
  fill: { mode: FillMode; color: string; opacity: number };
  doors: WallDoor[];
}

/** Common wall thicknesses, mm: stud wall, brick, wide stud or block, block, double brick. */
export const WALL_THICKNESSES = [90, 110, 150, 190, 230] as const;
/** The opening a new door gets, mm (a single door of 820 mm is the common Australian leaf). */
export const DOOR_WIDTH: Record<DoorKind, number> = { single: 820, double: 1640, roller: 2400 };
export const DOOR_WORDS: Record<DoorKind, string> = { single: "Single door", double: "Double door", roller: "Roller door" };
/** A door stays at least this far from a corner or an end of the wall, mm. */
export const DOOR_MARGIN = 0;

export const WALL_LINE: Style = {
  strokeColor: "#e8eaf0",
  strokeWidth: 1.5,
  widthMode: "screen",
  dash: "solid",
  fill: { mode: "hatch", color: "#9aa3b2", opacity: 0.8 },
  opacity: 1
};
const SPINE: Style = { ...WALL_LINE, strokeWidth: 0.75, dash: "dashdot", fill: { mode: "none", color: "#9aa3b2", opacity: 0 } };
const DOOR_LEAF: Style = { ...WALL_LINE, strokeWidth: 1.25, fill: { mode: "none", color: "#9aa3b2", opacity: 0 } };
const DOOR_SWING: Style = { ...DOOR_LEAF, strokeWidth: 0.75 };
const DOOR_DRUM: Style = { ...DOOR_LEAF, strokeWidth: 0.75, dash: "dashed" };

export const DEFAULT_WALL: Omit<WallMeta, "closed" | "doors"> = {
  thickness: 110,
  justify: "centre",
  fill: { ...WALL_LINE.fill }
};

// ------------------------------------------------------------------------------------- reading --

/** meta.wall of a wall group, or null. */
export function wallMetaOf(e: Entity | undefined): WallMeta | null {
  if (!e || e.type !== "group") return null;
  const w = e.meta?.wall as WallMeta | undefined;
  if (!w || typeof w.thickness !== "number" || !(w.thickness > 0) || !Array.isArray(w.doors)) return null;
  return w;
}

/** The spine of wall `id`: the points and closed flag of its first child, or null. */
export function spineOf(doc: Doc, id: string): { points: Vec[]; closed: boolean } | null {
  const g = doc.entities[id];
  if (!wallMetaOf(g)) return null;
  const s = doc.entities[(g as GroupEntity).children[0] ?? ""];
  if (!s || s.type !== "polyline" || s.points.length < 2) return null;
  return { points: s.points, closed: s.closed };
}

export const isWall = (doc: Doc, id: string): boolean => spineOf(doc, id) !== null;

// ------------------------------------------------------------------------------------ geometry --

const sub = (a: Vec, b: Vec): Vec => ({ x: a.x - b.x, y: a.y - b.y });
const add = (a: Vec, b: Vec): Vec => ({ x: a.x + b.x, y: a.y + b.y });
const mul = (a: Vec, k: number): Vec => ({ x: a.x * k, y: a.y * k });
const dot = (a: Vec, b: Vec): number => a.x * b.x + a.y * b.y;
const cross = (a: Vec, b: Vec): number => a.x * b.y - a.y * b.x;
const deg = (v: Vec): number => (Math.atan2(v.y, v.x) * 180) / Math.PI;

export interface WallSeg {
  index: number;
  a: Vec;
  b: Vec;
  /** unit direction a -> b */
  d: Vec;
  /** unit LEFT normal (screen, Y down) */
  n: Vec;
  len: number;
  /** spine distance from the start of the run to `a` */
  start: number;
  /** the part of the segment that is straight on BOTH faces (a door fits only here), along the segment */
  uMin: number;
  uMax: number;
}

export interface WallGeom {
  segs: WallSeg[];
  closed: boolean;
  /** the length of the spine */
  total: number;
  /** offsets of the left and right face from the spine, along the left normal */
  offL: number;
  offR: number;
  /** the face corner points, one per spine point */
  FL: Vec[];
  FR: Vec[];
  /** 3.23.0: the joins of the two ends of an open run (none: a square end) */
  joins: WallJoins;
}

/**
 * 3.23.0. How one open END of a wall run joins another wall. Found from the drawing each time (wallNetwork), not stored:
 * move a wall away and the join goes; put its end back and the join comes back.
 *   "mitre": the end meets the end of another run (or the other end of the same run) at the same point: the two walls
 *            meet in a mitre, like a corner inside one run. `d` is the other wall as if the run went on into it (the
 *            direction of travel), oL and oR its face offsets along the left normal of that direction.
 *   "butt":  the end is on another wall (a T): the end is cut at the near face of that wall. `deep` is the centre line of
 *            that wall: the other wall's face is hidden as far as there, so the joint has no line across it.
 */
export type EndJoin =
  | { kind: "mitre"; withId: string; d: Vec; oL: number; oR: number }
  | { kind: "butt"; withId: string; face: { p: Vec; d: Vec }; deep: { p: Vec; d: Vec } };
export interface WallJoins {
  start?: EndJoin;
  end?: EndJoin;
}

/** Where two lines meet (a point and a direction each); null when they are parallel. */
function lineX(p1: Vec, d1: Vec, p2: Vec, d2: Vec): Vec | null {
  const c = cross(d1, d2);
  if (Math.abs(c) < 1e-9) return null;
  return add(p1, mul(d1, cross(sub(p2, p1), d2) / c));
}

/** The spine points with repeated points removed (and the last point dropped when it closes on the first). */
function cleanPoints(points: Vec[], closed: boolean): Vec[] {
  const out: Vec[] = [];
  for (const p of points) if (!out.length || Math.hypot(p.x - out[out.length - 1]!.x, p.y - out[out.length - 1]!.y) > 1e-9) out.push(p);
  if (closed && out.length > 2 && Math.hypot(out[0]!.x - out[out.length - 1]!.x, out[0]!.y - out[out.length - 1]!.y) <= 1e-9) out.pop();
  return out;
}

/** The faces of a wall run; null for a spine with no length. `joins`: how the ends of an open run join other walls. */
export function wallGeometry(points: Vec[], closed: boolean, w: Pick<WallMeta, "thickness" | "justify">, joins: WallJoins = {}): WallGeom | null {
  const P = cleanPoints(points, closed);
  const isClosed = closed && P.length >= 3;
  if (P.length < 2) return null;
  const T = w.thickness;
  const offL = w.justify === "centre" ? T / 2 : w.justify === "left" ? 0 : T;
  const offR = offL - T;
  const segs: WallSeg[] = [];
  const count = isClosed ? P.length : P.length - 1;
  let start = 0;
  for (let i = 0; i < count; i++) {
    const a = P[i]!;
    const b = P[(i + 1) % P.length]!;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const d = { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
    segs.push({ index: i, a, b, d, n: { x: d.y, y: -d.x }, len, start, uMin: 0, uMax: len });
    start += len;
  }
  // 3.23.0: an open end that joins another wall: a mitre with it, or cut at its face (a T)
  const joined = (P0: Vec, own: WallSeg, o: number, face: "L" | "R", j: EndJoin | undefined): Vec => {
    const square = add(P0, mul(own.n, o));
    if (!j) return square;
    if (j.kind === "mitre") {
      const nv = { x: j.d.y, y: -j.d.x };
      const ov = face === "L" ? j.oL : j.oR;
      const m = lineX(add(P0, mul(nv, ov)), j.d, square, own.d);
      return m && Math.hypot(m.x - P0.x, m.y - P0.y) <= 4 * Math.max(Math.abs(o), Math.abs(ov), T) ? m : square;
    }
    const m = lineX(square, own.d, j.face.p, j.face.d);
    return m && Math.hypot(m.x - P0.x, m.y - P0.y) <= 12 * Math.max(Math.abs(o), T) ? m : square;
  };
  // the face corner at point k, at offset o: the face lines of the segments before and after it meet (a mitre)
  const corner = (k: number, o: number): Vec => {
    const before = isClosed ? segs[(k - 1 + count) % count] : segs[k - 1];
    const after = isClosed ? segs[k % count] : segs[k];
    const face = o === offL ? "L" : "R";
    if (!before) return joined(after!.a, after!, o, face, joins.start);
    if (!after) return joined(before.b, before, o, face, joins.end);
    const A1 = add(before.a, mul(before.n, o));
    const A2 = add(after.a, mul(after.n, o));
    const c = cross(before.d, after.d);
    if (Math.abs(c) < 1e-9) return A2; // in line
    const t = cross(sub(A2, A1), after.d) / c;
    const m = add(A1, mul(before.d, t));
    // a very sharp corner: keep the corner near the point (a long spike reads as an error)
    const lim = 4 * Math.max(Math.abs(o), T);
    return Math.hypot(m.x - P[k]!.x, m.y - P[k]!.y) > lim ? A2 : m;
  };
  const FL = P.map((_, k) => corner(k, offL));
  const FR = P.map((_, k) => corner(k, offR));
  for (const s of segs) {
    const k0 = s.index;
    const k1 = (s.index + 1) % P.length;
    const along = (F: Vec[], k: number) => dot(sub(F[k]!, s.a), s.d);
    s.uMin = Math.max(along(FL, k0), along(FR, k0), 0);
    s.uMax = Math.min(along(FL, k1), along(FR, k1), s.len);
  }
  return { segs, closed: isClosed, total: start, offL, offR, FL, FR, joins: isClosed ? {} : joins };
}

/** Where a door opens on its segment, kept inside the straight part; null when it does not fit there. */
export function doorSpan(g: WallGeom, door: WallDoor): { seg: WallSeg; ua: number; ub: number; uc: number } | null {
  const seg = g.segs[door.seg];
  if (!seg || !(door.width > 0)) return null;
  const lo = seg.uMin + DOOR_MARGIN;
  const hi = seg.uMax - DOOR_MARGIN;
  if (hi - lo < door.width - 1e-9) return null;
  const uc = Math.min(hi - door.width / 2, Math.max(lo + door.width / 2, door.at));
  return { seg, ua: uc - door.width / 2, ub: uc + door.width / 2, uc };
}

/** A face point at spine distance `g` (0..total; a closed run wraps). */
function faceAt(geo: WallGeom, g: number, off: number): Vec {
  const t = geo.closed ? ((g % geo.total) + geo.total) % geo.total : g;
  let s = geo.segs[geo.segs.length - 1]!;
  for (const x of geo.segs) {
    if (t <= x.start + x.len + 1e-9) {
      s = x;
      break;
    }
  }
  return add(add(s.a, mul(s.n, off)), mul(s.d, Math.min(s.len, Math.max(0, t - s.start))));
}

/** The face from spine distance ga to gb (gb may pass the end of a closed run): its cut points and the corners between. */
function faceBetween(geo: WallGeom, F: Vec[], off: number, ga: number, gb: number): Vec[] {
  // 3.23.0: at an end of an open run the face ends at its corner point (a mitre or a T cut), not square
  const atEnd = (g: number): Vec | null => (geo.closed ? null : g <= 1e-9 ? F[0]! : g >= geo.total - 1e-9 ? F[F.length - 1]! : null);
  const out = [atEnd(ga) ?? faceAt(geo, ga, off)];
  const laps = geo.closed ? 2 : 1;
  for (let lap = 0; lap < laps; lap++) {
    for (let k = 0; k < F.length; k++) {
      const at = (geo.closed ? geo.segs[k]!.start : k === 0 ? 0 : geo.segs[k - 1]!.start + geo.segs[k - 1]!.len) + lap * geo.total;
      if (at > ga + 1e-9 && at < gb - 1e-9) out.push(F[k]!);
    }
  }
  out.push(atEnd(gb) ?? faceAt(geo, gb, off));
  return out;
}

/**
 * 3.23.0: "piece" is the FILL of the wall between two openings (no line of its own); "edge" is a line of the outline.
 * The outline is drawn as edges so that the part of a line inside another wall is not drawn: walls that join or cross
 * show one clean outline. (Before 3.23.0 a piece was a filled, outlined polygon; such a wall is drawn again.)
 */
export type WallPart =
  | { type: "polyline"; role: "piece" | "edge" | "door"; points: Vec[]; closed: boolean; style: Style }
  | { type: "arc"; role: "door"; center: Vec; r: number; startDeg: number; sweepDeg: number; style: Style };

/** What the other walls do to a wall: how its ends join, and the bodies of the walls near it (their quads). */
export interface WallContext {
  joins?: WallJoins;
  /** each a convex-ish quad of another wall's body (one per segment); a line inside one is not drawn */
  hide?: Vec[][];
}

// ------------------------------------------------------------------------------- hidden lines --

/** True when p is inside polygon q by more than a small margin (on its boundary is not inside). */
function insideBy(p: Vec, q: Vec[], margin = 1e-4): boolean {
  let c = false;
  for (let i = 0, j = q.length - 1; i < q.length; j = i++) {
    const a = q[i]!, b = q[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) c = !c;
  }
  if (!c) return false;
  for (let i = 0, j = q.length - 1; i < q.length; j = i++) {
    const a = q[i]!, b = q[j]!;
    const ab = sub(b, a);
    const L2 = dot(ab, ab);
    const t = L2 > 0 ? Math.max(0, Math.min(1, dot(sub(p, a), ab) / L2)) : 0;
    if (Math.hypot(p.x - (a.x + ab.x * t), p.y - (a.y + ab.y * t)) <= margin) return false;
  }
  return true;
}

/** The parameters (0..1) where segment a-b crosses the edges of polygon q. */
function crossings(a: Vec, b: Vec, q: Vec[]): number[] {
  const out: number[] = [];
  const d = sub(b, a);
  for (let i = 0, j = q.length - 1; i < q.length; j = i++) {
    const c = q[j]!, e = sub(q[i]!, c);
    const den = cross(d, e);
    if (Math.abs(den) < 1e-12) continue;
    const t = cross(sub(c, a), e) / den;
    const u = cross(sub(c, a), d) / den;
    if (t > 1e-9 && t < 1 - 1e-9 && u >= -1e-9 && u <= 1 + 1e-9) out.push(t);
  }
  return out;
}

/**
 * The parts of a line (open or closed) that are not inside any of the quads. A line with nothing hidden comes back as
 * it is (closed stays closed); a cut line comes back as open lines.
 */
export function visibleRuns(points: Vec[], closed: boolean, quads: readonly Vec[][]): Array<{ points: Vec[]; closed: boolean }> {
  const pts = closed ? [...points, points[0]!] : points;
  const runs: Vec[][] = [];
  let cur: Vec[] = [];
  let anyHidden = false;
  const close = () => {
    if (cur.length >= 2) runs.push(cur);
    cur = [];
  };
  for (let i = 0; i + 1 < pts.length; i++) {
    const a = pts[i]!, b = pts[i + 1]!;
    const lo = { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) };
    const hi = { x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) };
    const near = quads.filter((q) => !(Math.max(...q.map((p) => p.x)) < lo.x || Math.min(...q.map((p) => p.x)) > hi.x || Math.max(...q.map((p) => p.y)) < lo.y || Math.min(...q.map((p) => p.y)) > hi.y));
    const ts = [0, ...near.flatMap((q) => crossings(a, b, q)), 1].sort((x, y) => x - y);
    for (let k = 0; k + 1 < ts.length; k++) {
      const t0 = ts[k]!, t1 = ts[k + 1]!;
      if (t1 - t0 < 1e-12) continue;
      const mid = add(a, mul(sub(b, a), (t0 + t1) / 2));
      if (near.some((q) => insideBy(mid, q))) {
        anyHidden = true;
        close();
        continue;
      }
      if (!cur.length) cur.push(add(a, mul(sub(b, a), t0)));
      cur.push(add(a, mul(sub(b, a), t1)));
    }
  }
  close();
  if (!anyHidden) return [{ points, closed }];
  // a closed line cut in places: the run that ends at the start point goes on into the run that starts there
  if (closed && runs.length > 1 && samePt(runs[0]![0]!, pts[0]!) && samePt(runs[runs.length - 1]![runs[runs.length - 1]!.length - 1]!, pts[0]!)) {
    const last = runs.pop()!;
    runs[0] = [...last, ...runs[0]!.slice(1)];
  }
  return runs.map((r) => ({ points: r, closed: false }));
}
const samePt = (a: Vec, b: Vec) => Math.abs(a.x - b.x) <= 1e-9 && Math.abs(a.y - b.y) <= 1e-9;

/** The body of a wall as one quad per segment (its face corners), with each T end reaching to the other wall's centre line. */
export function bodyQuads(geo: WallGeom): Vec[][] {
  const FL = [...geo.FL];
  const FR = [...geo.FR];
  if (!geo.closed) {
    const n = FL.length - 1;
    const deep = (j: EndJoin | undefined, k: number, s: WallSeg) => {
      if (j?.kind !== "butt") return;
      const l = lineX(FL[k]!, s.d, j.deep.p, j.deep.d);
      const r = lineX(FR[k]!, s.d, j.deep.p, j.deep.d);
      if (l && r) {
        FL[k] = l;
        FR[k] = r;
      }
    };
    deep(geo.joins.start, 0, geo.segs[0]!);
    deep(geo.joins.end, n, geo.segs[geo.segs.length - 1]!);
  }
  return geo.segs.map((s) => {
    const k0 = s.index;
    const k1 = (s.index + 1) % FL.length;
    return [FL[k0]!, FL[k1]!, FR[k1]!, FR[k0]!];
  });
}

/** The look of the parts: the lines of the wall and of its doors (the fill comes from meta.wall). */
export interface WallLook {
  piece: Style;
  leaf: Style;
  swing: Style;
  drum: Style;
}

export const DEFAULT_LOOK: WallLook = { piece: WALL_LINE, leaf: DOOR_LEAF, swing: DOOR_SWING, drum: DOOR_DRUM };

/**
 * Every derived part of a wall: the fill pieces between the openings, the lines of its outline, then the door symbols.
 * `ctx` (3.23.0): the joins of its ends and the bodies of the walls near it (wallNetwork). A line of the outline inside
 * another wall is not drawn, and a joined end has no line across it.
 */
export function wallParts(points: Vec[], closed: boolean, w: WallMeta, look: WallLook = DEFAULT_LOOK, ctx: WallContext = {}): WallPart[] {
  const geo = wallGeometry(points, closed, w, ctx.joins);
  if (!geo) return [];
  // the fill has no line of its own (opacity 0 is the stroke only); the lines are the edges
  const pieceStyle: Style = { ...look.piece, opacity: 0, fill: { ...w.fill } };
  const edgeStyle: Style = { ...look.piece, opacity: look.piece.opacity > 0 ? look.piece.opacity : 1, fill: { ...look.piece.fill, mode: "none" } };
  const hide = ctx.hide ?? [];
  const edges: WallPart[] = [];
  const edge = (pts: Vec[], isClosed = false) => {
    for (const r of visibleRuns(pts, isClosed, hide)) edges.push({ type: "polyline", role: "edge", points: r.points, closed: r.closed, style: structuredClone(edgeStyle) });
  };
  const out: WallPart[] = [];
  const spans = w.doors
    .map((d) => ({ door: d, span: doorSpan(geo, d) }))
    .filter((x): x is { door: WallDoor; span: NonNullable<ReturnType<typeof doorSpan>> } => !!x.span)
    .map((x) => ({ ...x, ga: x.span.seg.start + x.span.ua, gb: x.span.seg.start + x.span.ub }))
    .sort((a, b) => a.ga - b.ga);
  // drop a door that overlaps the one before it
  const open: typeof spans = [];
  for (const s of spans) if (!open.length || s.ga >= open[open.length - 1]!.gb - 1e-9) open.push(s);

  // the jamb or end lines: across the wall at a door, and at an end of an open run unless that end joins a wall
  const atStart = (ga: number) => !geo.closed && ga <= 1e-6;
  const atEnd = (gb: number) => !geo.closed && gb >= geo.total - 1e-6;
  const piece = (ga: number, gb: number) => {
    if (gb - ga <= 1e-6) return;
    const L = faceBetween(geo, geo.FL, geo.offL, ga, gb);
    const R = faceBetween(geo, geo.FR, geo.offR, ga, gb);
    out.push({ type: "polyline", role: "piece", points: [...L, ...[...R].reverse()], closed: true, style: structuredClone(pieceStyle) });
    edge(L);
    edge(R);
    if (!(atStart(ga) && geo.joins.start)) edge([L[0]!, R[0]!]);
    if (!(atEnd(gb) && geo.joins.end)) edge([L[L.length - 1]!, R[R.length - 1]!]);
  };
  if (!open.length) {
    if (geo.closed) {
      // a ring: round the left face, across at the first corner, round the right face the other way (the fill only:
      // the lines are the two face rings, so there is no line across the wall)
      const L = [...geo.FL, geo.FL[0]!];
      const R = [...geo.FR, geo.FR[0]!];
      out.push({ type: "polyline", role: "piece", points: [...L, ...R.reverse()], closed: true, style: structuredClone(pieceStyle) });
      edge(geo.FL, true);
      edge(geo.FR, true);
    } else piece(0, geo.total);
  } else if (geo.closed) {
    for (let i = 0; i < open.length; i++) {
      const next = open[(i + 1) % open.length]!;
      piece(open[i]!.gb, next.ga + (i === open.length - 1 ? geo.total : 0));
    }
  } else {
    piece(0, open[0]!.ga);
    for (let i = 0; i < open.length; i++) piece(open[i]!.gb, i + 1 < open.length ? open[i + 1]!.ga : geo.total);
  }
  out.push(...edges);
  for (const s of open) out.push(...doorSymbol(geo, s.door, s.span, look));
  return out;
}

/** The arc from direction v1 round to direction v2 (the short way), about c. */
function arcBetween(c: Vec, r: number, v1: Vec, v2: Vec, style: Style): WallPart {
  const a1 = deg(v1);
  const sweep = ((((deg(v2) - a1 + 540) % 360) + 360) % 360) - 180;
  return { type: "arc", role: "door", center: c, r, startDeg: ((a1 % 360) + 360) % 360, sweepDeg: sweep === 0 ? 90 : sweep, style: structuredClone(style) };
}

/** The plan symbol of a door in its opening (ua..ub on the segment). */
function doorSymbol(geo: WallGeom, door: WallDoor, span: { seg: WallSeg; ua: number; ub: number; uc: number }, look: WallLook): WallPart[] {
  const s = span.seg;
  const off = door.side > 0 ? geo.offL : geo.offR; // the face on the side the door opens to
  const F = (u: number): Vec => add(add(s.a, mul(s.n, off)), mul(s.d, u));
  const out = mul(s.n, door.side); // away from the wall, to that side
  const w = span.ub - span.ua;
  const line = (pts: Vec[], style: Style, closed = false): WallPart => ({ type: "polyline", role: "door", points: pts, closed, style: structuredClone(style) });
  if (door.kind === "single") {
    const uh = door.hinge === "start" ? span.ua : span.ub;
    const uo = door.hinge === "start" ? span.ub : span.ua;
    const H = F(uh);
    return [line([H, add(H, mul(out, w))], look.leaf), arcBetween(H, w, sub(F(uo), H), out, look.swing)];
  }
  if (door.kind === "double") {
    const half = w / 2;
    const Ha = F(span.ua);
    const Hb = F(span.ub);
    return [
      line([Ha, add(Ha, mul(out, half))], look.leaf),
      arcBetween(Ha, half, s.d, out, look.swing),
      line([Hb, add(Hb, mul(out, half))], look.leaf),
      arcBetween(Hb, half, mul(s.d, -1), out, look.swing)
    ];
  }
  // roller: the curtain across the opening on that face, and the drum (dashed) beside the wall, a little wider
  const drum = Math.min(450, Math.max(250, Math.round(0.15 * w)));
  const g = 50;
  const a = F(span.ua - g);
  const b = F(span.ub + g);
  return [line([F(span.ua), F(span.ub)], look.leaf), line([a, b, add(b, mul(out, drum)), add(a, mul(out, drum))], look.drum, true)];
}

// ------------------------------------------------------------------------------------- building --

function toEntity(p: WallPart, layerId: string): Entity {
  const meta = { wallPart: p.role };
  if (p.type === "polyline") return { id: newId(), type: "polyline", layerId, locked: false, hidden: false, points: p.points, closed: p.closed, style: p.style, meta } as PolylineEntity;
  return { id: newId(), type: "arc", layerId, locked: false, hidden: false, center: p.center, r: p.r, startDeg: p.startDeg, sweepDeg: p.sweepDeg, style: p.style, meta } as ArcEntity;
}

/** A new wall run as ONE command: the spine, its parts and the group. Null for a spine with no length. */
export function makeWallCommand(doc: Doc, layerId: string, points: Vec[], closed: boolean, w: Omit<WallMeta, "closed">, look: WallLook = DEFAULT_LOOK): { cmd: Command; groupId: string } | null {
  const pts = cleanPoints(points, closed);
  const isClosed = closed && pts.length >= 3;
  if (pts.length < 2) return null;
  const meta: WallMeta = { ...structuredClone(w), closed: isClosed };
  const spine: PolylineEntity = { id: newId(), type: "polyline", layerId, locked: false, hidden: true, points: pts, closed: isClosed, style: structuredClone(SPINE), meta: { wallPart: "spine" } };
  const parts = wallParts(pts, isClosed, meta, look).map((p) => toEntity(p, layerId));
  const kids = [spine, ...parts];
  const group: GroupEntity = { id: newId(), type: "group", layerId, locked: false, hidden: false, children: kids.map((k) => k.id), meta: { wall: meta } };
  void doc;
  return {
    cmd: {
      kind: "batch",
      label: "add wall",
      commands: [
        { kind: "addEntities", entities: kids, z: kids.map(() => ({ layerId, index: -1 })) },
        { kind: "group", group, z: { layerId, index: -1 }, children: kids.map((k) => ({ id: k.id, z: { layerId, index: -1 } })) }
      ]
    },
    groupId: group.id
  };
}

/** The look of a wall as drawn now: the style of its first piece, first leaf, first arc and first dashed door line. */
export function lookOf(doc: Doc, g: GroupEntity): WallLook {
  const kids = g.children.slice(1).map((c) => doc.entities[c]).filter((e): e is Entity => !!e && "style" in e);
  const st = (e: Entity | undefined) => (e as (Entity & { style: Style }) | undefined)?.style;
  const role = (e: Entity) => (e.meta?.wallPart as string | undefined) ?? "";
  // 3.23.0: the line look is on the edges; a wall drawn before 3.23.0 has it on its pieces. A fill piece has no line
  // (opacity 0): its look is the line look at full opacity.
  const edge = st(kids.find((e) => role(e) === "edge"));
  const piece = st(kids.find((e) => role(e) === "piece"));
  const line = edge ?? (piece ? { ...piece, opacity: piece.opacity > 0 ? piece.opacity : 1 } : undefined);
  const doorLines = kids.filter((e) => role(e) === "door" && e.type === "polyline");
  return {
    piece: line ?? DEFAULT_LOOK.piece,
    leaf: st(doorLines.find((e) => (e as PolylineEntity).style.dash !== "dashed")) ?? DEFAULT_LOOK.leaf,
    swing: st(kids.find((e) => role(e) === "door" && e.type === "arc")) ?? DEFAULT_LOOK.swing,
    drum: st(doorLines.find((e) => (e as PolylineEntity).style.dash === "dashed")) ?? DEFAULT_LOOK.drum
  };
}

const nearPt = (a: Vec, b: Vec) => Math.abs(a.x - b.x) <= 1e-6 && Math.abs(a.y - b.y) <= 1e-6;
const nearAng = (a: number, b: number) => Math.abs((((a - b) % 360) + 540) % 360 - 180) <= 1e-6;

function sameStyle(a: Style, b: Style): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** True when the entity is the part as drawn now (same kind, place and look). */
function samePart(e: Entity, p: WallPart): boolean {
  if (e.type !== p.type || (e.meta?.wallPart ?? "") !== p.role || !sameStyle((e as Entity & { style: Style }).style, p.style)) return false;
  if (p.type === "polyline" && e.type === "polyline") return e.closed === p.closed && e.points.length === p.points.length && e.points.every((q, i) => nearPt(q, p.points[i]!));
  if (p.type === "arc" && e.type === "arc") {
    if (!nearPt(e.center, p.center) || Math.abs(e.r - p.r) > 1e-6) return false;
    // the same arc may be stored from its other end
    const same = nearAng(e.startDeg, p.startDeg) && Math.abs(e.sweepDeg - p.sweepDeg) <= 1e-6;
    const flipped = nearAng(e.startDeg + e.sweepDeg, p.startDeg + p.sweepDeg) && nearAng(e.startDeg, p.startDeg + p.sweepDeg) && Math.abs(e.sweepDeg + p.sweepDeg) <= 1e-6;
    return same || flipped;
  }
  return false;
}

// ------------------------------------------------------------------------------ the network (3.23.0) --

/** One wall as the network sees it. */
interface NetWall {
  id: string;
  points: Vec[];
  closed: boolean;
  w: WallMeta;
  base: WallGeom;
  box: { minX: number; minY: number; maxX: number; maxY: number };
}

/** How near two ends must be to join, and an end to a wall, mm. */
export const JOIN_TOL = 0.5;

/**
 * 3.23.0. Every wall of the drawing, how the ends of each join other walls, and the body quads of each. Owner,
 * 2026-10-21: "walls can attach to other walls and the intersection needs to look neat". `override` puts other
 * points (and doors) on one wall: the network of a wall that is being dragged.
 */
export function wallNetwork(doc: Doc, override?: { id: string; points: Vec[]; closed?: boolean; doors?: WallDoor[] }): Map<string, { joins: WallJoins; geo: WallGeom; quads: Vec[][]; box: NetWall["box"] }> {
  const walls: NetWall[] = [];
  for (const e of Object.values(doc.entities)) {
    if (e.type !== "group" || e.hidden) continue;
    const w0 = wallMetaOf(e);
    const sp = spineOf(doc, e.id);
    if (!w0 || !sp) continue;
    const ov = override?.id === e.id ? override : null;
    const points = cleanPoints(ov ? ov.points : sp.points, ov?.closed ?? sp.closed);
    const closed = (ov?.closed ?? sp.closed) && points.length >= 3;
    const w = ov?.doors ? { ...w0, doors: ov.doors } : w0;
    const base = wallGeometry(points, closed, w);
    if (!base) continue;
    const xs = [...base.FL, ...base.FR].map((p) => p.x);
    const ys = [...base.FL, ...base.FR].map((p) => p.y);
    const pad = w.thickness;
    walls.push({ id: e.id, points, closed, w, base, box: { minX: Math.min(...xs) - pad, minY: Math.min(...ys) - pad, maxX: Math.max(...xs) + pad, maxY: Math.max(...ys) + pad } });
  }
  walls.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const near = (a: Vec, b: Vec) => Math.hypot(a.x - b.x, a.y - b.y) <= JOIN_TOL;
  const inBox = (p: Vec, b: NetWall["box"]) => p.x >= b.minX && p.x <= b.maxX && p.y >= b.minY && p.y <= b.maxY;

  // the join of one end: P the end point, `travel` the direction of travel there (start: into the run; end: out of it)
  const joinOf = (r: NetWall, atStart: boolean): EndJoin | undefined => {
    const segs = r.base.segs;
    const P = atStart ? r.points[0]! : r.points[r.points.length - 1]!;
    const own = atStart ? segs[0]! : segs[segs.length - 1]!;
    const out = atStart ? mul(own.d, -1) : own.d; // out of the run at this end
    const inside = add(P, mul(out, -Math.min(10, own.len / 2)));
    // a T on segment `seg` of wall `s`: this end is cut at the face of `s` on its own side
    const buttOn = (s: NetWall, seg: WallSeg): EndJoin => {
      const c = (s.base.offL + s.base.offR) / 2;
      const side = dot(sub(inside, seg.a), seg.n) - c >= 0 ? 1 : -1;
      return { kind: "butt", withId: s.id, face: { p: add(seg.a, mul(seg.n, c + side * (s.w.thickness / 2))), d: seg.d }, deep: { p: add(seg.a, mul(seg.n, c)), d: seg.d } };
    };
    // 1. the ends of open runs at the same point (also the other end of this run)
    const ends: Array<{ s: NetWall; seg: WallSeg; j: EndJoin & { kind: "mitre" }; straight: boolean }> = [];
    for (const s of walls) {
      if (s.closed || !inBox(P, s.box)) continue;
      const sStart = s.points[0]!;
      const sEnd = s.points[s.points.length - 1]!;
      const ss = s.base.segs;
      const self = s.id === r.id;
      for (const otherAtStart of [true, false]) {
        if (self && otherAtStart === atStart) continue;
        if (!near(otherAtStart ? sStart : sEnd, P)) continue;
        const seg = otherAtStart ? ss[0]! : ss[ss.length - 1]!;
        // the other wall as if the run went on into it: travelling through the joint
        //   this END + other START: on along the other's direction; this END + other END: back along it (faces swap)
        //   this START + other END: in along the other's direction; this START + other START: back along it
        const forward = atStart ? !otherAtStart : otherAtStart;
        const d = forward ? seg.d : mul(seg.d, -1);
        const oL = forward ? s.base.offL : -s.base.offR;
        const oR = forward ? s.base.offR : -s.base.offL;
        if (Math.abs(cross(d, own.d)) < 1e-9 && dot(d, own.d) < 0) continue; // folded back on itself: no join
        ends.push({ s, seg, j: { kind: "mitre", withId: s.id, d, oL, oR }, straight: Math.abs(cross(d, own.d)) < 1e-9 });
      }
    }
    // an end that goes on in a straight line: the two join as one wall (it gives way to a T on a wall through here)
    const straight = ends.find((x) => x.straight);
    const mitre: EndJoin | undefined = straight?.j;
    if (!straight && ends.length >= 2) {
      // two other ends in line with each other (a wall split here): this end is a T on that wall
      const through = ends.find((a) => ends.some((b) => b !== a && Math.abs(cross(a.j.d, b.j.d)) < 1e-9));
      if (through) return buttOn(through.s, through.seg);
    }
    if (!straight && ends.length) return ends[0]!.j; // a corner of two runs: a mitre
    // 2. on the body of another wall (or of this run, away from this end): a T, cut at its near face. A wall that only
    //    ENDS here is not a wall through here: the ends above deal with it.
    let best: { j: EndJoin; v: number } | null = null;
    for (const s of walls) {
      if (!inBox(P, s.box)) continue;
      if (s.id !== r.id && !s.closed && (near(s.points[0]!, P) || near(s.points[s.points.length - 1]!, P))) continue;
      const c = (s.base.offL + s.base.offR) / 2;
      const half = s.w.thickness / 2;
      for (const seg of s.base.segs) {
        if (s.id === r.id && (seg.index === own.index || Math.abs(seg.index - own.index) === 1)) continue;
        const u = dot(sub(P, seg.a), seg.d);
        const v = dot(sub(P, seg.a), seg.n) - c;
        if (u < -JOIN_TOL || u > seg.len + JOIN_TOL || Math.abs(v) > half + JOIN_TOL) continue;
        if (Math.abs(cross(own.d, seg.d)) < 1e-6) continue; // along the other wall: not a T
        if (!best || Math.abs(v) < best.v) best = { j: buttOn(s, seg), v: Math.abs(v) };
      }
    }
    return best?.j ?? mitre;
  };

  const out = new Map<string, { joins: WallJoins; geo: WallGeom; quads: Vec[][]; box: NetWall["box"] }>();
  for (const r of walls) {
    const joins: WallJoins = r.closed ? {} : { start: joinOf(r, true), end: joinOf(r, false) };
    const geo = wallGeometry(r.points, r.closed, r.w, joins) ?? r.base;
    out.set(r.id, { joins, geo, quads: bodyQuads(geo), box: r.box });
  }
  return out;
}

/** The context of wall `id` in network `net`: its joins and the body quads of the walls near it. */
export function contextIn(net: ReturnType<typeof wallNetwork>, id: string): WallContext {
  const me = net.get(id);
  if (!me) return {};
  const hide: Vec[][] = [];
  for (const [oid, o] of net) {
    if (oid === id) continue;
    const b = o.box, m = me.box;
    if (b.maxX < m.minX || b.minX > m.maxX || b.maxY < m.minY || b.minY > m.maxY) continue;
    hide.push(...o.quads);
  }
  return { joins: me.joins, hide };
}

/** The parts of wall `id` as the drawing has it now (or with other points: a wall being dragged), with its joins. */
export function wallPartsInDoc(doc: Doc, id: string, override?: { points: Vec[]; closed?: boolean; doors?: WallDoor[] }): WallPart[] {
  const g = doc.entities[id];
  const w = wallMetaOf(g);
  const spine = spineOf(doc, id);
  if (!w || !spine || g?.type !== "group") return [];
  const net = wallNetwork(doc, override ? { id, ...override } : undefined);
  const points = override?.points ?? spine.points;
  const closed = override?.closed ?? spine.closed;
  return wallParts(points, closed, override?.doors ? { ...w, doors: override.doors } : w, lookOf(doc, g), contextIn(net, id));
}

/** The command that draws wall `id` again from its spine and meta, or null when it is right already. */
export function wallRebuildCommand(doc: Doc, id: string, net: ReturnType<typeof wallNetwork> = wallNetwork(doc)): Command | null {
  const g = doc.entities[id];
  const w = wallMetaOf(g);
  const spine = spineOf(doc, id);
  if (!w || !spine || g?.type !== "group") return null;
  const parts = wallParts(spine.points, spine.closed, w, lookOf(doc, g), contextIn(net, id));
  const kids = g.children.slice(1).map((c) => doc.entities[c]);
  if (kids.length === parts.length && kids.every((k, i) => !!k && samePart(k, parts[i]!))) return null;
  const layerId = doc.entities[g.children[0]!]?.layerId ?? g.layerId;
  // the same parts in kind and order: change them in place
  if (kids.length === parts.length && kids.every((k, i) => !!k && k.type === parts[i]!.type)) {
    const patches = kids.map((k, i) => {
      const p = parts[i]!;
      const e = k as Entity & { style: Style };
      if (p.type === "polyline" && e.type === "polyline")
        return { id: e.id, before: { points: e.points, closed: e.closed, style: e.style, meta: e.meta }, after: { points: p.points, closed: p.closed, style: p.style, meta: { ...(e.meta ?? {}), wallPart: p.role } } };
      const a = e as unknown as ArcEntity;
      const pa = p as Extract<WallPart, { type: "arc" }>;
      return { id: a.id, before: { center: a.center, r: a.r, startDeg: a.startDeg, sweepDeg: a.sweepDeg, style: a.style, meta: a.meta }, after: { center: pa.center, r: pa.r, startDeg: pa.startDeg, sweepDeg: pa.sweepDeg, style: pa.style, meta: { ...(a.meta ?? {}), wallPart: pa.role } } };
    });
    return { kind: "updateEntities", patches: structuredClone(patches) };
  }
  // other parts (a door added or taken away): the group is made again with the same id and the same spine
  const made = parts.map((p) => toEntity(p, layerId));
  const commands: Command[] = [makeUngroupCommand(doc, id)];
  const old = g.children.slice(1).filter((c) => doc.entities[c]);
  if (old.length) commands.push(makeDeleteCommand(doc, old));
  if (made.length) commands.push({ kind: "addEntities", entities: made, z: made.map(() => ({ layerId, index: -1 })) });
  const children = [g.children[0]!, ...made.map((e) => e.id)];
  commands.push({
    kind: "group",
    group: { ...structuredClone(g), children },
    z: { layerId: g.layerId, index: (doc.zOrder[g.layerId] ?? []).indexOf(id) },
    children: children.map((c) => ({ id: c, z: { layerId: doc.entities[c]?.layerId ?? layerId, index: -1 } }))
  });
  return { kind: "batch", label: "wall", commands };
}

/** Commands that cannot change a wall. */
const NO_GEOMETRY = new Set<Command["kind"]>(["setDocProps", "addAsset", "removeAsset", "updateLayer", "reorderLayer", "addLayer", "deleteLayer"]);

/** For DocStore.deriver: draws again every wall whose parts do not match its spine and meta (one undo step with `cmd`). */
export function wallDeriver(doc: Doc, cmd: Command): Command | null {
  if (NO_GEOMETRY.has(cmd.kind)) return null;
  const out: Command[] = [];
  let net: ReturnType<typeof wallNetwork> | null = null;
  for (const e of Object.values(doc.entities)) {
    if (e.type !== "group" || !e.meta?.wall) continue;
    net ??= wallNetwork(doc); // once per command: a wall's joins depend on the walls round it
    const c = wallRebuildCommand(doc, e.id, net);
    if (c) out.push(c);
  }
  if (!out.length) return null;
  return out.length === 1 ? out[0]! : { kind: "batch", label: "walls", commands: out };
}
